import { ArrowUpRight, Mail, MessageCircle } from "lucide-react";

/* ─────────────────────────────────────────────
   CTABanner — template partagé (style "Carnet ouvert")
   Fond sombre · Aurora glow · pulse dot · pill Aurora
   ───────────────────────────────────────────── */
export function CTABanner({
  testId,
  eyebrowText,
  titleStart,
  titleAccent,
  subText,
  children,
}) {
  return (
    <section
      data-testid={testId}
      className="px-6 md:px-10 py-14 md:py-20"
    >
      <div
        className="mx-auto max-w-6xl rounded-3xl md:rounded-[2rem] p-10 md:p-16 relative overflow-hidden text-center"
        style={{ background: "#0B1027" }}
      >
        {/* Aurora glows */}
        <div
          className="absolute -top-32 -left-20 w-72 h-72 rounded-full opacity-50 blur-3xl pointer-events-none"
          style={{
            background: "radial-gradient(circle, #A855F7, transparent)",
          }}
        />
        <div
          className="absolute -bottom-32 -right-20 w-72 h-72 rounded-full opacity-40 blur-3xl pointer-events-none"
          style={{
            background: "radial-gradient(circle, #EC4899, transparent)",
          }}
        />

        <div className="relative">
          {eyebrowText && (
            <span className="inline-flex items-center gap-2 text-[#C4B5FD] text-xs uppercase tracking-[0.2em] font-bold">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              {eyebrowText}
            </span>
          )}
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mt-4 leading-[1.1] text-white">
            {titleStart}{" "}
            <em className="not-italic italic font-light text-aurora">
              {titleAccent}
            </em>
          </h3>
          {subText && (
            <p className="text-white/80 mt-5 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              {subText}
            </p>
          )}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

/* Reusable button styles for the banner */
const primaryPill =
  "inline-flex items-center justify-center gap-2 btn-aurora text-white font-semibold px-7 h-14 rounded-full shadow-tactile-lg hover-lift transition text-base whitespace-nowrap";

const secondaryPillOnDark =
  "btn-outline-aurora inline-flex items-center justify-center gap-2 text-white border border-white/30 font-medium px-6 h-14 rounded-full text-sm whitespace-nowrap";

/* ─────────────────────────────────────────────
   Checkpoint 1 — APRÈS PORTFOLIO
   ───────────────────────────────────────────── */
export function CTAAfterPortfolio() {
  return (
    <CTABanner
      testId="cta-checkpoint-portfolio"
      eyebrowText="Et le vôtre ?"
      titleStart="Inspiré par ces projets ?"
      titleAccent="Le vôtre commence ici."
      subText="30 minutes pour échanger sur votre projet. Gratuit, sans engagement."
    >
      <a
        href="#reservation"
        data-testid="cta-portfolio-link"
        className={primaryPill}
      >
        Réserver un appel
        <ArrowUpRight size={16} />
      </a>
    </CTABanner>
  );
}

/* ─────────────────────────────────────────────
   Checkpoint 2 — APRÈS MÉTHODE (référence "Carnet ouvert")
   ───────────────────────────────────────────── */
export function CTAAfterMethod() {
  return (
    <CTABanner
      testId="cta-checkpoint-method"
      eyebrowText="Carnet ouvert"
      titleStart="Prêt à concrétiser"
      titleAccent="votre projet ?"
      subText="30 minutes pour comprendre, conseiller et chiffrer. Gratuit · Sans engagement · Visio ou téléphone."
    >
      <a
        href="#reservation"
        data-testid="cta-method-link"
        className={primaryPill}
      >
        Réserver un appel
        <ArrowUpRight size={16} />
      </a>
    </CTABanner>
  );
}

/* ─────────────────────────────────────────────
   Checkpoint 3 — APRÈS TESTIMONIALS
   ───────────────────────────────────────────── */
export function CTAAfterTestimonials() {
  return (
    <CTABanner
      testId="cta-checkpoint-testimonials"
      eyebrowText="Rejoignez-les"
      titleStart="À votre tour"
      titleAccent="de prendre la parole."
      subText="30 minutes pour échanger sur votre projet. Visio ou téléphone, sans engagement."
    >
      <a
        href="#reservation"
        data-testid="cta-testimonials-calendar"
        className={primaryPill}
      >
        Réserver un appel
        <ArrowUpRight size={16} />
      </a>
    </CTABanner>
  );
}

/* ─────────────────────────────────────────────
   Sticky Mobile CTA — inchangé (cas particulier mobile)
   ───────────────────────────────────────────── */
export function StickyMobileCTA() {
  return (
    <div
      data-testid="sticky-mobile-cta"
      className="md:hidden fixed bottom-3 left-3 right-3 z-40"
    >
      <div className="rounded-full bg-white shadow-tactile-lg p-2 border border-[#0B1027]/5 flex items-center gap-2">
        <a
          href="mailto:contact@numedia.be"
          aria-label="Email"
          data-testid="sticky-cta-email"
          className="h-11 w-11 rounded-full bg-[#F3F0FF] flex items-center justify-center text-[#A855F7] shrink-0"
        >
          <Mail size={18} />
        </a>
        <a
          href="https://wa.me/32498979847"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          data-testid="sticky-cta-whatsapp"
          className="h-11 w-11 rounded-full bg-[#E6F8EF] flex items-center justify-center text-[#25D366] shrink-0"
        >
          <MessageCircle size={18} />
        </a>
        <a
          href="#reservation"
          data-testid="sticky-cta-calendar"
          className="flex-1 btn-aurora text-white font-semibold text-sm py-3 rounded-full text-center"
        >
          Réserver un appel
        </a>
      </div>
    </div>
  );
}
